/**
 * Staging state machine for the PQR Preview page.
 *
 * The page holds two snapshots:
 *   - `serverSnapshot` — what the DB currently has, captured at load.
 *   - `stagedSnapshot` — what the user has applied via section modals but
 *     not yet persisted. The preview canvas always renders this.
 *
 * Each modal owns a local working copy; on Apply it calls `apply(section,
 * snapshot)` to commit the change into `stagedSnapshot`. `isDirty(section)`
 * powers per-region amber indicators on the canvas; `isDirty()` powers the
 * global "unsaved changes" banner and the toolbar's Discard button visibility.
 *
 * Persistence is one direction: the page calls `markServerSaved(snapshot,
 * version)` after a successful Save / Save & Generate, which advances both
 * `serverSnapshot` and `sectionsVersion` to the newly-persisted state and
 * clears all dirty flags.
 */
import { useCallback, useMemo, useState } from 'react'
import {
  buildPqrPreviewDiff,
  buildPqrPreviewSignificantDiff,
  type PqrEditableSnapshot,
  type PqrPreviewDiff
} from '../../../utils/pqrPreviewDiff'

/**
 * Section regions whose values can be staged on the PQR preview canvas.
 *
 * Sections that have migrated to the navigate-to-source flow (per
 * pqr-preview-field-navigation) are intentionally absent:
 *   - `'comments'`         — sourced from FloorSheet `operators.notes` (Slice 03)
 *   - `'notes'`            — Welders nav to FloorSheet (Slice 01),
 *                            Equipment navs to Project edit (Slice 04).
 *                            Server still reads legacy overrides for
 *                            data saved before the migration.
 *   - `'materials'`        — Per-pipe row navs to WpsBaseMaterial (Slice 05).
 *   - `'jointPreparation'` — Panel navs to WpsBevelDetails (Slice 06).
 *                            Server still reads legacy overrides for
 *                            data saved before the migration.
 *   - `'weldingVariables'` — Per-cell navs to WpsWeldDetails (Slice 07).
 *                            Server still reads legacy overrides for
 *                            data saved before the migration.
 *   - `'weldingParameters'` — Per-cell navs to per-weld WeldParameters
 *                             page (Slice 08). Server still reads
 *                             legacy overrides for data saved before
 *                             the migration.
 */
export type PqrSectionName =
  | 'identifiers'
  | 'signatures'

/**
 * Shape of the snapshot patch each section commits on Apply. Only the keys
 * that section owns are required; everything else is left untouched on the
 * staged snapshot.
 */
export type PqrSectionPatch =
  | { section: 'identifiers'; pqrNumber: string; revision: string }
  | {
      section: 'signatures'
      preparedBy: PqrEditableSnapshot['preparedBy']
      reviewedBy: PqrEditableSnapshot['reviewedBy']
    }

export interface PqrPreviewStaging {
  serverSnapshot: PqrEditableSnapshot
  stagedSnapshot: PqrEditableSnapshot
  sectionsVersion: number
  hasPersistedPqr: boolean
  /** `true` iff `stagedSnapshot` differs from `serverSnapshot` in any section. */
  isDirty: (section?: PqrSectionName) => boolean
  /** Memoised diff between server and staged. */
  stagedDiff: PqrPreviewDiff
  /** Diff excluding PQR No./Rev. — powers confirm / discard / banner UX. */
  stagedSignificantDiff: PqrPreviewDiff
  /** True when staged edits beyond document numbers warrant warnings. */
  hasSignificantChanges: () => boolean
  /** Apply a section patch into the staged snapshot. */
  apply: (patch: PqrSectionPatch) => void
  /** Reset staged snapshot back to the server snapshot. */
  discardAll: () => void
  /** Reset only one section back to the server snapshot. */
  discardSection: (section: PqrSectionName) => void
  /**
   * After a successful save: replace both server and staged with the freshly
   * persisted snapshot, update the optimistic-locking version, and mark
   * `hasPersistedPqr` (so subsequent saves send ifMatchVersion).
   */
  markServerSaved: (next: PqrEditableSnapshot, version: number) => void
  /**
   * Replace the server baseline on initial load / reload-after-409.
   *
   * When `stagedOverride` is supplied, the staged snapshot is set to
   * that value instead of `next` — used by the new-draft picker handoff
   * so the picker-supplied identifiers appear as a *staged edit on top
   * of the persisted server snapshot* (dirty banner + leave-guard
   * active) rather than as a clean baseline. Calling `reset` with the
   * same `(next, stagedOverride)` pair is idempotent, which is what
   * makes the picker handoff survive React 18 `<StrictMode>`'s
   * double-invoked mount effect in dev.
   */
  reset: (
    next: PqrEditableSnapshot,
    version: number,
    hasPqr: boolean,
    stagedOverride?: PqrEditableSnapshot
  ) => void
}

/**
 * Project the per-section view of a snapshot. Used by `isDirty(section)` to
 * compare just the keys a given section owns, without false positives from
 * unrelated sections.
 */
function projectSection(snapshot: PqrEditableSnapshot, section: PqrSectionName): unknown {
  switch (section) {
    case 'identifiers':
      return { pqrNumber: snapshot.pqrNumber, revision: snapshot.revision }
    case 'signatures':
      return { preparedBy: snapshot.preparedBy, reviewedBy: snapshot.reviewedBy }
  }
}

function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (a === null || b === null) return a === b
  if (typeof a !== 'object' || typeof b !== 'object') return false
  return JSON.stringify(a) === JSON.stringify(b)
}

function applyPatch(snapshot: PqrEditableSnapshot, patch: PqrSectionPatch): PqrEditableSnapshot {
  switch (patch.section) {
    case 'identifiers':
      return { ...snapshot, pqrNumber: patch.pqrNumber, revision: patch.revision }
    case 'signatures':
      return { ...snapshot, preparedBy: patch.preparedBy, reviewedBy: patch.reviewedBy }
  }
}

export interface UsePqrPreviewStagingArgs {
  initialServer: PqrEditableSnapshot
  initialVersion: number
  initialHasPersistedPqr: boolean
}

export function usePqrPreviewStaging(args: UsePqrPreviewStagingArgs): PqrPreviewStaging {
  const [server, setServer] = useState<PqrEditableSnapshot>(args.initialServer)
  const [staged, setStaged] = useState<PqrEditableSnapshot>(args.initialServer)
  const [version, setVersion] = useState<number>(args.initialVersion)
  const [hasPersistedPqr, setHasPersistedPqr] = useState<boolean>(args.initialHasPersistedPqr)

  const apply = useCallback((patch: PqrSectionPatch) => {
    setStaged((prev) => applyPatch(prev, patch))
  }, [])

  const discardAll = useCallback(() => {
    setStaged(server)
  }, [server])

  const discardSection = useCallback(
    (section: PqrSectionName) => {
      setStaged((prev) => {
        switch (section) {
          case 'identifiers':
            return { ...prev, pqrNumber: server.pqrNumber, revision: server.revision }
          case 'signatures':
            return { ...prev, preparedBy: server.preparedBy, reviewedBy: server.reviewedBy }
        }
      })
    },
    [server]
  )

  const markServerSaved = useCallback((next: PqrEditableSnapshot, nextVersion: number) => {
    setServer(next)
    setStaged(next)
    setVersion(nextVersion)
    setHasPersistedPqr(true)
  }, [])

  const reset = useCallback(
    (
      next: PqrEditableSnapshot,
      nextVersion: number,
      nextHasPqr: boolean,
      stagedOverride?: PqrEditableSnapshot
    ) => {
      setServer(next)
      setStaged(stagedOverride ?? next)
      setVersion(nextVersion)
      setHasPersistedPqr(nextHasPqr)
    },
    []
  )

  const stagedDiff = useMemo(() => buildPqrPreviewDiff(server, staged), [server, staged])
  const stagedSignificantDiff = useMemo(
    () => buildPqrPreviewSignificantDiff(server, staged),
    [server, staged]
  )

  const hasSignificantChanges = useCallback(
    () => stagedSignificantDiff.hasChanges,
    [stagedSignificantDiff]
  )

  const isDirty = useCallback(
    (section?: PqrSectionName) => {
      if (section) {
        return !deepEqual(projectSection(server, section), projectSection(staged, section))
      }
      return stagedDiff.hasChanges
    },
    [server, staged, stagedDiff]
  )

  return {
    serverSnapshot: server,
    stagedSnapshot: staged,
    sectionsVersion: version,
    hasPersistedPqr,
    isDirty,
    stagedDiff,
    stagedSignificantDiff,
    hasSignificantChanges,
    apply,
    discardAll,
    discardSection,
    markServerSaved,
    reset
  }
}
